import { ErrorMessage } from "@hookform/error-message";
import {
  ProgramType,
  ProgramTypeDisplayName,
} from "pmt-types/src/enums/ProgramType.ts";
import {
  SourceStatus,
  SourceStatusDisplayName,
} from "pmt-types/src/enums/SourceStatus.ts";
import { Source, SourceSchema } from "pmt-types/src/models/Source.ts";
import { Fragment, useState } from "react";
import { useQueryClient } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import PolicyList from "../../components/app/policyList.tsx";
import Sidebar from "../../components/app/sidebar.tsx";
import ErrorLabel from "../../components/form/errorLabel.tsx";
import FormInput from "../../components/form/formInput.tsx";
import PmtForm from "../../components/form/pmtForm.tsx";
import SearchableMultiSelect from "../../components/form/searchableMultiSelect.tsx";
import FormRoot from "../../components/form/style/formRoot.tsx";
import FormSection from "../../components/form/style/formSection.tsx";
import TabbedSelect from "../../components/form/tabbedSelect.tsx";
import TextArea from "../../components/form/textArea.tsx";
import LoadingSpinner from "../../components/loadingSpinner.tsx";
import NotFoundPage from "../../global/notFoundPage.tsx";
import { useMutationSearchFirstNamedInsureds } from "../../hooks/mutations/useMutationSearchFirstNamedInsureds.ts";
import { useMutationSearchProducingOffices } from "../../hooks/mutations/useMutationSearchProducingOffices.ts";
import { useMutationUpsertSource } from "../../hooks/mutations/useMutationUpsertSource.ts";
import { QueryKeys } from "../../hooks/queries/queryKeys.ts";
import { useQueryGetSourceById } from "../../hooks/queries/useQueryGetSourceById.ts";
import useQueryParams from "../../hooks/useQueryParams.ts";
import enumToLabelValue from "../../utils/transforms/enumToLabelValue.ts";
import { transformSelectInput } from "../../utils/transforms/transformSelectInput.ts";
import { Maybe } from "../../utils/types/maybe.ts";
import ExcessLiabilityPolicyForm from "./components/excessLiabilityPolicyForm.tsx";
import GeneralLiabilityPolicyForm from "./components/generalLiabilityPolicyForm.tsx";

const defaultSourceValues: Partial<Source> = {
  id: "new",
  producingOffices: ["R.K. Gore & Associates, LLC"],
};

export default function EditSource() {
  const navigate = useNavigate();
  const { sourceId } = useParams();
  const queryClient = useQueryClient();
  const queryParams = useQueryParams();
  const [currentSource, setCurrentSource] = useState<Maybe<Source>>(null);

  const [showGeneralLiabilitySidebar, setShowGeneralLiabilitySidebar] =
    useState(false);
  const [excessLiabilitySidebarIndex, setExcessLiabilitySidebarIndex] =
    useState<Maybe<number>>(null);

  const { data: source, isFetching: isSourceFetching } =
    useQueryGetSourceById(sourceId);

  const { mutateAsync: searchFirstNamedInsureds } =
    useMutationSearchFirstNamedInsureds();
  const { mutateAsync: searchProducingOffices } =
    useMutationSearchProducingOffices();
  const { mutate, isLoading: isSubmitting } = useMutationUpsertSource();

  if (sourceId == null) {
    return <NotFoundPage />;
  }

  const isNew = sourceId === "new";

  const defaultFormValues =
    currentSource?.id != null && currentSource.id === sourceId
      ? currentSource
      : defaultSourceValues;

  if (isSourceFetching) {
    return <LoadingSpinner />;
  }

  const navigateAfterSave = (source: Source) => {
    if (sourceId != null && queryParams.get("return") === "search") {
      navigate(`/sources`);
    } else {
      navigate(`/sources/${source.id}/details`);
    }
  };

  return (
    <>
      <PmtForm<Source>
        schema={SourceSchema}
        pageTitle={isNew ? "New Source" : source?.name ?? "Edit Source"}
        defaultValues={source ?? defaultFormValues}
        submitMutation={mutate}
        onSave={async (source) => {
          setCurrentSource(null);
          await queryClient.invalidateQueries({
            queryKey: [QueryKeys.GetSource, sourceId],
          });
          navigateAfterSave(source);
        }}
        onCancel={() => navigate(-1)}
        isSubmitting={isSubmitting}
        renderForm={(form) => (
          <Fragment>
            <FormRoot>
              <FormSection label={"Details"}>
                <FormInput name="name" label="Name" />
                <TabbedSelect
                  name="status"
                  label="Status"
                  tabs={enumToLabelValue(
                    SourceStatus.enum,
                    SourceStatusDisplayName,
                  )}
                />
                <TabbedSelect
                  name="programType"
                  label="Program Type"
                  tabs={enumToLabelValue(
                    ProgramType.enum,
                    ProgramTypeDisplayName,
                  )}
                />
                <SearchableMultiSelect
                  name="firstNamedInsureds"
                  label="First Named Insured"
                  transform={transformSelectInput}
                  queryFn={searchFirstNamedInsureds}
                />
                <SearchableMultiSelect
                  name="producingOffices"
                  label="Producing Office"
                  transform={transformSelectInput}
                  queryFn={searchProducingOffices}
                />
                <TextArea name="description" label="Description" />
              </FormSection>
              <FormSection label={"Coverage"}>
                <PolicyList
                  generalLiability={form.getValues("generalLiability")}
                  excessLiabilities={form.getValues("excessLiabilities")}
                  onAddGeneralLiabilityPolicy={() => {
                    setShowGeneralLiabilitySidebar(true);
                  }}
                  onEditGeneralLiabilityPolicy={() => {
                    setShowGeneralLiabilitySidebar(true);
                  }}
                  onAddExcessLiabilityPolicy={() => {
                    setExcessLiabilitySidebarIndex(-1);
                  }}
                  onEditExcessLiabilityPolicy={(index) => {
                    setExcessLiabilitySidebarIndex(index);
                  }}
                />
                <ErrorMessage
                  name={"generalLiability"}
                  render={({ message }) => <ErrorLabel message={message} />}
                />
                <ErrorMessage
                  name={"excessLiabilities"}
                  render={({ message }) => <ErrorLabel message={message} />}
                />
              </FormSection>
              <FormSection>
                <TextArea name="notes" label="Notes" />
              </FormSection>
            </FormRoot>
            {showGeneralLiabilitySidebar && (
              <Sidebar
                header={"General Liability Policy"}
                onClose={() => setShowGeneralLiabilitySidebar(false)}
              >
                <GeneralLiabilityPolicyForm
                  source={form.getValues()}
                  onSubmit={(policy) => {
                    form.setValue("generalLiability", policy);
                    setShowGeneralLiabilitySidebar(false);
                  }}
                  onCancel={() => setShowGeneralLiabilitySidebar(false)}
                />
              </Sidebar>
            )}
            {excessLiabilitySidebarIndex != null && (
              <Sidebar
                header={"Excess Liability Policy"}
                onClose={() => setExcessLiabilitySidebarIndex(null)}
              >
                <ExcessLiabilityPolicyForm
                  source={form.getValues()}
                  policyIndex={excessLiabilitySidebarIndex}
                  onSubmit={(policy) => {
                    let excessLiabilityPolicies =
                      form.getValues("excessLiabilities") ?? [];

                    if (
                      excessLiabilitySidebarIndex === -1 ||
                      excessLiabilitySidebarIndex == null
                    ) {
                      excessLiabilityPolicies.push(policy);
                    } else {
                      excessLiabilityPolicies = [
                        ...excessLiabilityPolicies.slice(
                          0,
                          excessLiabilitySidebarIndex,
                        ),
                        policy,
                        ...excessLiabilityPolicies.slice(
                          excessLiabilitySidebarIndex + 1,
                        ),
                      ];
                    }

                    form.setValue("excessLiabilities", excessLiabilityPolicies);
                    setExcessLiabilitySidebarIndex(null);
                  }}
                  onCancel={() => setExcessLiabilitySidebarIndex(null)}
                  onDelete={() => {
                    let excessLiabilityPolicies =
                      form.getValues("excessLiabilities") ?? [];

                    if (
                      excessLiabilitySidebarIndex === -1 ||
                      excessLiabilitySidebarIndex == null
                    ) {
                      return;
                    }

                    excessLiabilityPolicies = [
                      ...excessLiabilityPolicies.slice(
                        0,
                        excessLiabilitySidebarIndex,
                      ),
                      ...excessLiabilityPolicies.slice(
                        excessLiabilitySidebarIndex + 1,
                      ),
                    ];

                    form.setValue("excessLiabilities", excessLiabilityPolicies);
                    setExcessLiabilitySidebarIndex(null);
                  }}
                />
              </Sidebar>
            )}
          </Fragment>
        )}
      />
    </>
  );
}
